<template>
    <div class="tw-max-w-sm">
        <div class="tw-space-y-8">
            <div class="tw-select-none">
                <div v-if="isNPSScale" class="tw-px-10px">
                    <div class="tw-flex tw-justify-between tw-items-end">
                        <div
                            v-for="score in selectableOptions"
                            :key="score"
                            class="tw-bg-gray-200 tw-w-5 tw-rounded tw-transition-colors tw-duration-500 tw-cursor-pointer ignore-enter-submit"
                            :style="`height: ${10 + (score / 10) * 4}px`"
                            :class="{ 'score-bg': isColorizedBar(score) }"
                            data-test-class="survey-input-score"
                            @click="onSelectScore($event, score)"
                            @keyup.enter="onSelectScore($event, score)"
                        ></div>
                    </div>
                    <div
                        class="tw-flex tw-justify-between tw-text-sm tw-text-gray-400 tw-tabular-nums tw-mt-1"
                    >
                        <div
                            v-for="score in selectableOptions"
                            :key="score"
                            class="tw-text-center tw-w-5 tw-cursor-pointer focus:score-text ignore-enter-submit"
                            tabindex="0"
                            @click="onSelectScore($event, score)"
                            @keyup.enter="onSelectScore($event, score)"
                        >
                            {{ score / 10 }}
                        </div>
                    </div>
                </div>
                <div class="tw-w-full tw-mt-6 tw-mb-4 tw-relative">
                    <div v-if="!isNPSScale" class="scale-points">
                        <div class="tw-flex tw-justify-between tw-items-end">
                            <div
                                v-for="score in selectableOptions"
                                :key="score"
                                class="tw-w-8 tw-h-8 tw-rounded-full tw-cursor-pointer ignore-enter-submit"
                                :class="[
                                    form.score === score
                                        ? 'tw-bg-transparent focus:tw-ring-transparent focus:tw-ring-offset-transparent'
                                        : isManuallySelected
                                        ? 'tw-bg-gray-200 focus:tw-ring-2 focus:tw-ring-gray-200 focus:tw-ring-offset-2'
                                        : 'tw-bg-gray-100 focus:tw-ring-2 focus:tw-ring-gray-100 focus:tw-ring-offset-2',
                                ]"
                                data-test-class="survey-input-score"
                                tabindex="0"
                                @click="onSelectScore($event, score)"
                                @keyup.enter="onSelectScore($event, score)"
                            ></div>
                        </div>
                    </div>
                    <label class="tw-sr-only" for="range-selector">{{
                        $t("surveys.participate.scoreLabel")
                    }}</label>
                    <input
                        id="range-selector"
                        v-model="form.score"
                        class="slider"
                        :class="{ 'slider-used': isManuallySelected }"
                        :disabled="!isManuallySelected"
                        type="range"
                        min="0"
                        max="100"
                        :step="isNPSScale ? 10 : 25"
                        @change="onSaveAnswer"
                    />
                </div>

                <div class="tw-flex tw-justify-between tw-text-sm tw-text-gray-600">
                    <span class="tw-w-1/2">{{
                        isNPSScale
                            ? $t("surveys.participate.zeroScoreMeaning")
                            : $t(`surveys.participate.scaleLabels.${question.scaleType}.1`)
                    }}</span>
                    <span class="tw-w-1/2 tw-text-right">{{
                        isNPSScale
                            ? $t("surveys.participate.tenScoreMeaning")
                            : $t(`surveys.participate.scaleLabels.${question.scaleType}.5`)
                    }}</span>
                </div>
            </div>

            <div
                v-if="hasVisibleCommentInput"
                class="tw-pt-8 tw-border-0 tw-border-t tw-border-solid tw-border-gray-100"
            >
                <l-input-text
                    :id="`answer-comment-${question.skill._id}`"
                    label=""
                    type="textarea"
                    :value.sync="form.comment"
                    class="tw survey-answer-flow"
                    :optional="!question.isRequired"
                    :placeholder="$t('surveys.participate.commentPlaceholder')"
                    :show-text-format-hint="false"
                    data-test-id="survey-input-textarea"
                    input-class="survey-response-textarea"
                    @input="onSaveAnswer"
                    @focus="isTextareaInFocus = true"
                    @blur="isTextareaInFocus = false"
                />

                <div
                    class="tw-text-right tw-text-indigo-500 tw-mt-2 tw-leading-none"
                    :class="{ 'tw-opacity-25': !isTextareaInFocus }"
                >
                    <l-keyboard-hint-text :keys="['shift', 'enter']">
                        <span slot="postfix">
                            {{ $t("surveys.participate.toMakeALineBreak") }}</span
                        >
                    </l-keyboard-hint-text>
                </div>
            </div>
        </div>

        <slot />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ANSWER_TYPES } from "@/config/survey-answer-session";

import LInputText from "@/components/LInputText";
import LKeyboardHintText from "@/components/LKeyboardHintText";

export default {
    components: {
        LInputText,
        LKeyboardHintText,
    },
    props: {
        question: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isTextareaInFocus: false,
            selectableOptions:
                this.question.scaleType === "nps" || !this.question.scaleType
                    ? Array.from({ length: 11 }, (_, i) => i * 10)
                    : Array.from({ length: 5 }, (_, i) => i * 25),
            isManuallySelected: false,
            form: {
                score: -100,
                comment: "",
            },
        };
    },
    computed: {
        ...mapGetters({
            userAnswers: "surveys/answerSession/userAnswers",
        }),
        hasVisibleCommentInput() {
            return this.question?.skill.answerType === ANSWER_TYPES.STRUCTURED;
        },
        isNPSScale() {
            return this.question.scaleType === "nps" || !this.question.scaleType;
        },
    },
    created() {
        // prefill form from global state
        if (this.userAnswers?.[this.question?.skill?._id]) {
            this.form = { ...this.form, ...this.userAnswers?.[this.question.skill._id] };
            this.isManuallySelected = true;
        }
    },
    methods: {
        onSaveAnswer() {
            const payload = {
                questionId: this.question.skill._id,
                answer: { ...this.form },
            };

            // delete the default as it was not set by intention of the user
            if (!this.isManuallySelected) delete payload.answer.score;
            if (!this.hasVisibleCommentInput) {
                delete payload.answer.comment;
            } else {
                payload.answer.comment = payload.answer.comment?.trim() ?? "";
            }

            // validation
            if (this.question?.isRequired) {
                if (
                    (this.hasVisibleCommentInput && !payload.answer.comment) ||
                    payload.answer.score === undefined
                ) {
                    delete payload.answer;
                }
            } else {
                if (this.hasVisibleCommentInput && !payload.answer.comment) {
                    delete payload.answer.comment;
                }
            }

            this.$store.dispatch("surveys/answerSession/setAnswer", { payload });
        },
        onSelectScore(event, score) {
            // not matching type by intention because of different input
            if (this.form.score == score) {
                // remove selection
                this.isManuallySelected = false;
                this.form.score = -100;
            } else {
                this.form.score = score;
                this.isManuallySelected = true;
            }

            // to allow submit on enter, we remove the focus when no keyboard is used
            if (!(event instanceof KeyboardEvent) && !event.code) {
                if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
            }

            this.onSaveAnswer();
        },
        isColorizedBar(index = false) {
            if (index === false || !this.isManuallySelected) return false;

            return this.form.score >= index;
        },
    },
};
</script>

<style scoped>
.score-bg {
    @apply tw-bg-indigo-500;
}

.focus\:score-text:focus {
    @apply tw-text-indigo-500;
}

.slider {
    -webkit-appearance: none;
    width: 100%;
    height: 10px;
    border-radius: 0.5rem;
    background: #f9fafb;
    outline: none;
}

.slider-used {
    background: #f3f4f6;
}

/** need to be defined separately to work */
.slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    border: 6px solid #9ca3af;
    cursor: pointer;
    display: none;
    visibility: hidden;
}

.slider-used::-webkit-slider-thumb {
    @apply tw-border-indigo-500;
    display: block;
    visibility: visible;
}

.slider::-moz-range-thumb {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: white;
    border: 6px solid #9ca3af;
    cursor: pointer;
    display: none;
    visibility: hidden;
}

.slider-used::-moz-range-thumb {
    @apply tw-border-indigo-500;
    display: block;
    visibility: visible;
}

.scale-points {
    position: absolute;
    top: -11px;
    left: 0px;
    right: 0px;
}

.tw-tabular-nums {
    font-variant-numeric: tabular-nums;
}

.tw-px-10px {
    margin: 0 10px;
}
</style>
